import React from 'react';
import { Sparkles, Check } from 'lucide-react';
import { useActivePromotions, computeCartProgress } from '../hooks/usePromotions';

/**
 * Cart-progress reminder rendered inside the cart sidebar. Reads active
 * cart-surface promotions and shows progress toward the highest-priority
 * threshold-based promo. Renders nothing when no eligible promo exists
 * or when the cart is empty.
 */
const CartPromoProgress = ({ items }) => {
  const { promos } = useActivePromotions('cart');

  if (!promos || promos.length === 0) return null;
  if (!items || items.length === 0) return null;

  // Prefer promos with a real threshold so progress bars are meaningful;
  // if none, fall back to the highest-priority no-minimum promo (shown as
  // an "eligible now" callout without a progress bar).
  const thresholdPromo = promos.find(p => (p.discount?.minimum_qualifying_subtotal || 0) > 0);
  const promo = thresholdPromo || promos[0];
  if (!promo) return null;

  const { eligibleSubtotal, remaining, progressPct, qualifies } = computeCartProgress(promo, items);
  const code = promo.code || '';
  const threshold = Number(promo.discount?.minimum_qualifying_subtotal || 0);
  const headline = promo.content?.public_headline || promo.name;

  return (
    <div
      data-testid="cart-promo-progress"
      style={{
        background: 'linear-gradient(135deg, rgba(7, 26, 51, 0.06) 0%, rgba(0, 174, 239, 0.08) 100%)',
        border: '1px solid rgba(0, 174, 239, 0.35)',
        borderRadius: '10px',
        padding: '12px 14px',
        margin: '8px 0 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: threshold > 0 ? '6px' : 0 }}>
        {qualifies ? (
          <Check size={16} style={{ color: '#0057B8', flexShrink: 0 }} aria-hidden="true" />
        ) : (
          <Sparkles size={16} style={{ color: '#00AEEF', flexShrink: 0 }} aria-hidden="true" />
        )}
        <div style={{ fontSize: '12.5px', color: '#071A33', lineHeight: 1.45, flex: 1 }}>
          {qualifies ? (
            <span data-testid="cart-promo-qualifies">
              You’re qualified for{' '}
              <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{code}</strong>
              {' '}— apply it at checkout to save.
            </span>
          ) : threshold > 0 ? (
            <span data-testid="cart-promo-remaining">
              Add <strong>${remaining.toFixed(2)}</strong> more of eligible items to unlock{' '}
              <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{code}</strong>
              {' '}— {headline}.
            </span>
          ) : (
            <span data-testid="cart-promo-no-min">
              Save with{' '}
              <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{code}</strong>
              {' '}— {headline}. Apply at checkout.
            </span>
          )}
        </div>
      </div>
      {threshold > 0 && (
        <div
          data-testid="cart-promo-progress-bar"
          style={{
            width: '100%',
            height: '6px',
            background: 'rgba(199, 211, 223, 0.4)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #0057B8, #00AEEF)',
              transition: 'width 0.35s ease',
            }}
          />
        </div>
      )}
      {threshold > 0 && (
        <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563', textAlign: 'right' }}>
          ${eligibleSubtotal.toFixed(2)} / ${threshold.toFixed(0)}
        </div>
      )}
    </div>
  );
};

export default CartPromoProgress;
